import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { SavedRepliesQuery } from './useSavedRepliesQuery'

// The codegen `SavedRepliesQueryQuery` masks the query's `@_unmask` fragments
// behind `$fragmentRefs`, hiding the scalar fields this cache update reads and
// writes; deriving the result via `ResultOf` inlines them.
type SavedRepliesQueryQuery = ResultOf<typeof SavedRepliesQuery>

type SavedReply = Extract<
  ResultOf<typeof CreateSavedReply>['createSavedReply'],
  { savedReply: unknown }
>['savedReply']

export type CreateSavedReply = (params: {
  title?: string | null
  content: string
  hotkeys?: string[]
}) => Promise<SavedReply>

export const CreateSavedReply = graphql(/* GraphQL */ `
  mutation CreateSavedReply($input: CreateSavedReplyInput!) {
    createSavedReply(input: $input) {
      ... on SavedReplyActionSuccess {
        savedReply {
          id
          organizationId
          title
          content {
            text
          }
          hotkeys
          createdAt
          updatedAt
        }
      }
      ... on MutationError {
        __typename
        message
      }
    }
  }
`)

/**
 * Hook to create a new saved reply. The new saved reply is added to the cache
 * to avoid data refetching.
 *
 * @returns {CreateSavedReply} Function to create a new saved reply.
 * @example
 * const createSavedReply = useCreateSavedReply();
 *
 * const handleCreateSavedReply = async () => {
 *   try {
 *     const newSavedReply = await createSavedReply({ title: 'New Saved Reply', content: 'This is a new saved reply' });
 *
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useCreateSavedReply(): CreateSavedReply {
  const organizationId = useCurrentOrganization()?.id ?? ''

  const [createSavedReplyMutation] = useMutation(CreateSavedReply, {
    update(cache, { data }) {
      if (
        !data?.createSavedReply ||
        data.createSavedReply.__typename !== 'SavedReplyActionSuccess'
      )
        return

      const newItem = data.createSavedReply.savedReply

      const variables = {
        input: { organizationId },
      }

      // Read from the cache with correct variables
      const existingData = cache.readQuery<SavedRepliesQueryQuery>({
        query: SavedRepliesQuery,
        variables,
      })

      if (!existingData) return

      // `__typename` is required by Apollo at write time but isn't part of the
      // query's selection set (so it's absent from the derived type). Building
      // the payload as a separate object keeps it out of the excess-property
      // check while preserving the runtime shape Apollo needs.
      const updatedData = {
        savedReplies: {
          __typename: 'SavedRepliesResponse',
          savedReplies: [newItem, ...existingData.savedReplies.savedReplies],
        },
      }

      cache.writeQuery({
        query: SavedRepliesQuery,
        variables,
        data: updatedData,
      })
    },
  })

  const createSavedReply = useCallback(
    async ({
      title,
      content,
      hotkeys,
    }: {
      title?: string | null
      content: string
      hotkeys?: string[]
    }): Promise<SavedReply> => {
      if (!organizationId) {
        throw new Error('Organization Id is required')
      }

      const { data, error } = await createSavedReplyMutation({
        variables: {
          input: {
            organizationId,
            savedReply: {
              title,
              content: {
                text: content,
              },
              hotkeys,
            },
          },
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to create saved reply')
      }

      const result = data.createSavedReply

      if (result.__typename !== 'SavedReplyActionSuccess') {
        throw new Error(
          'message' in result && typeof result.message === 'string'
            ? result.message
            : 'Failed to create saved reply',
        )
      }

      return result.savedReply
    },
    [createSavedReplyMutation, organizationId],
  )

  return createSavedReply
}
